<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  // The board's names for the statuses (Pipeline#status_labels): a lead board closes a card as
  // "Locado" or "Perdido", so its actions say so. A board that names none resolves and cancels.
  statusLabels: { type: Object, default: () => ({}) },
  // Ends the menu with "Excluir", apart from the outcomes; administrators only (CardPolicy).
  deletable: { type: Boolean, default: false },
});

const emit = defineEmits(['resolve', 'cancel', 'delete']);

const { t } = useI18n();
const isOpen = ref(false);

const actionLabel = (status, fallbackKey) =>
  props.statusLabels?.[status]
    ? t('CARDS.SHOW.MARK_AS', { status: props.statusLabels[status] })
    : t(fallbackKey);

// The way a card usually ends is the button itself; the other one, and the deletion, wait in
// the menu beside it.
const resolveLabel = computed(() =>
  actionLabel('resolved', 'CARDS.SHOW.RESOLVE')
);
// A phone has no room for "Marcar como" beside the card's title: the outcome alone.
const resolveShortLabel = computed(
  () => props.statusLabels?.resolved || t('CARDS.SHOW.RESOLVE')
);

const menuSections = computed(() => [
  {
    items: [
      {
        label: actionLabel('canceled', 'CARDS.SHOW.CANCEL'),
        value: 'cancel',
        action: 'close',
        icon: 'i-lucide-circle-x',
      },
    ],
  },
  ...(props.deletable
    ? [
        {
          items: [
            {
              label: t('CARDS.SHOW.DELETE'),
              value: 'delete',
              action: 'delete',
              icon: 'i-lucide-trash-2',
            },
          ],
        },
      ]
    : []),
]);

const onAction = ({ value }) => {
  isOpen.value = false;
  if (value === 'delete') emit('delete');
  else emit('cancel');
};
</script>

<template>
  <div v-on-clickaway="() => (isOpen = false)" class="relative flex">
    <Button
      icon="i-lucide-circle-check-big"
      color="teal"
      size="sm"
      class="ltr:!rounded-r-none rtl:!rounded-l-none"
      :title="resolveLabel"
      data-testid="card-resolve"
      @click="emit('resolve')"
    >
      <span class="min-w-0 truncate sm:hidden">{{ resolveShortLabel }}</span>
      <span class="hidden min-w-0 truncate sm:inline">{{ resolveLabel }}</span>
    </Button>
    <Button
      icon="i-lucide-chevron-down"
      color="teal"
      size="sm"
      class="ltr:!rounded-l-none rtl:!rounded-r-none ltr:border-l rtl:border-r border-white/30"
      :title="$t('CARDS.SHOW.MORE_ACTIONS')"
      data-testid="card-close-menu"
      @click="isOpen = !isOpen"
    />
    <DropdownMenu
      v-if="isOpen"
      :menu-sections="menuSections"
      class="z-50 w-max min-w-44 max-w-72 mt-1 top-full ltr:right-0 rtl:left-0"
      @action="onAction"
    />
  </div>
</template>
